import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, Member, ObjEntity, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { type BackrefGroup, groupsOf, type LinkColumns } from "../../lib/backrefs.ts";
import { useListView } from "../../lib/cardView.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useResource } from "../../lib/resource.ts";
import { Button } from "../../ui/Button.tsx";
import { Fold } from "../../ui/Fold.tsx";
import { Async } from "../../ui/misc.tsx";
import { Table } from "../../ui/Table.tsx";
import { ViewToggle } from "../../ui/ViewToggle.tsx";
import { ItemCards } from "../items/cards.tsx";
import { EntityLabel } from "../items/entityLabel.tsx";
import { ItemsHead, Rows } from "../items/rows.tsx";

/** How many more items a group shows each time it's asked. */
const PAGE_SIZE = 20;
/** The most a group shows here (past that, "Open as a list"). */
const MAX_SHOWN = 200;

interface GroupProps {
  space: SpaceOut;
  item: ItemDetail;
  group: BackrefGroup;
  entities: readonly ObjEntity[];
  members: readonly Member[] | undefined;
  cards: boolean;
}

/**
 * One group: the items of an entity linking here through one field, as the items list shows them (that entity's
 * columns, the values each link carries, but not the field pointing here), folded as a tree's group (a short one
 * open), more on asking; its name opens them as a list of their own.
 */
function Group({ space, item, group, entities, members, cards }: GroupProps) {
  const entity = entities.find((e) => e.id === group.entity);
  const field = entity?.fields?.find((f) => f.key === group.key);
  const via = t("item.backrefs.via", { field: field?.label || group.key });
  return (
    <div class="mb-4">
      <Fold
        name={`${entity?.name ?? t("items.noEntity")} ${via}`}
        label={
          <>
            <span class="font-medium text-zinc-900">
              <EntityLabel entity={entity} large={true} />
            </span>
            <span class="text-zinc-500">{via}</span>
          </>
        }
        count={group.count}
        href={spaceUrl(
          space,
          `/items?${new URLSearchParams({ entity: group.entity ?? "none", refers_to: `${group.key}:${item.id}` })}`,
        )}
      >
        {() => <GroupItems {...{ space, item, group, entities, members, cards }} />}
      </Fold>
    </div>
  );
}

/** A group's items, the newest changed first, more added below on asking. */
function GroupItems({ space, item, group, entities, members, cards }: GroupProps) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const page = useResource(`backrefs:${space.id}:${item.id}:${group.entity ?? ""}:${group.key}:${limit}`, (signal) =>
    api.get<PageItemRow>(
      spaceApi(space, "/items"),
      { refers_to: `${group.key}:${item.id}`, entity: group.entity ?? "none", sort: "-updated", limit },
      signal,
    ),
  );
  const entity = entities.find((e) => e.id === group.entity);
  const field = entity?.fields?.find((f) => f.key === group.key);
  const link: LinkColumns | undefined = field?.fields?.length
    ? { to: item.id, key: group.key, fields: field.fields }
    : undefined;
  const props = { space, entity, entities, members, hide: group.key, link };
  return (
    <Async r={page}>
      {(p) => (
        <>
          {cards ? (
            <ItemCards page={p} {...props} />
          ) : (
            <Table>
              <ItemsHead entity={entity} hide={group.key} link={link} />
              <Rows page={p} {...props} />
            </Table>
          )}
          {p.next && limit < MAX_SHOWN ? (
            <div class="mt-2">
              <Button size="sm" onClick={() => setLimit(Math.min(limit + PAGE_SIZE, MAX_SHOWN))}>
                {t("items.tree.more")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Async>
  );
}

/**
 * The items linking to this one, below it: a group per entity and field they link through (the largest first),
 * each as a table or cards (the items list's choice).
 */
export function ReferencedBy(p: {
  space: SpaceOut;
  item: ItemDetail;
  entities: readonly ObjEntity[];
  members: readonly Member[] | undefined;
}) {
  const [view, setView] = useListView();
  const groups = groupsOf(p.item.referenced_by);
  if (groups.length === 0) {
    return null;
  }
  const cards = view !== "table";
  return (
    <div>
      <div class="mb-3 flex">
        <ViewToggle views={["table", "cards"]} view={cards ? "cards" : "table"} onView={setView} />
      </div>
      {groups.map((g) => (
        <Group key={`${g.entity ?? ""}:${g.key}`} {...p} group={g} cards={cards} />
      ))}
    </div>
  );
}
